import { ReactNode, useEffect, useState } from 'react';
import { NodeBody, StepIcon, TriggerBody } from './editor/EditorNode';
import {
  acceptedOf,
  edgesOf,
  EMAIL_STAT_LABEL,
  Graph,
  JourneyStats,
  layoutGraph,
  NODE_H,
  NODE_W,
  outcomeLabel,
  Step,
  STEP_LABEL,
  StepStats,
  stepsInOrder,
  stepSummary,
  StepType,
  TRIGGER_LABEL,
  triggerText,
} from './model';
import { CHANNEL_LABEL, type Channel } from '../channels/model';
import { currentLocale, t } from '../lib/i18n';
import { setJourneyI18n } from './i18nBridge';
setJourneyI18n({ t, locale: currentLocale });

/**
 * The journey canvas (docs/journeys/SPEC.md §4.1): read-only, laid out top-down by DAG levels. Edges are an SVG layer;
 * nodes are HTML on top of it (focusable buttons, text that wraps, an outcomes list that opens). Editing with the
 * mouse, touch and keyboard is the editor (./editor/EditorCanvas.tsx, docs/pilot/SPEC.md §12); this stays read-only.
 * Shared by the dashboard and «Маяк» (apps/admin imports it through `@web/journeys`).
 */

export type Mark = 'added' | 'changed' | 'error' | 'warning';
export const TRIGGER_NODE = '__trigger';

export { StepIcon };

const n0 = (n: number) => n.toLocaleString(currentLocale());

function NodeStats({ s, type }: { s?: StepStats; type: StepType }) {
  if (!s) return null;
  const outcomes = Object.entries(s.outcomes).sort((a, b) => b[1] - a[1]);
  return (
    <>
      <div className="jc-stats">
        <span title={t('Участия, которые сейчас на этом шаге')}>{t('сейчас')} <b>{n0(s.now)}</b></span>
        <span title={t('Сколько раз шаг завершился')}>{t('прошло')} <b>{n0(s.entered)}</b></span>
        {s.held > 0 && <span className="warn" title={t('Удержания: пауза, тихие часы, стоп-краны')}>{t('удержано {n}', { n: n0(s.held) })}</span>}
        {s.errors > 0 && <span className="bad">{t('ошибки {n}', { n: n0(s.errors) })}</span>}
      </div>
      {type === 'send_email' && s.emails && (
        <div className="jc-mail" title={t('«Принято» — провайдер взял письмо; «доставлено» — только по событию провайдера')}>
          {t('принято {a} · доставлено {b} · отказы {c} · жалобы {d}', { a: n0(acceptedOf(s.emails)), b: n0(s.emails.delivered ?? 0), c: n0(s.emails.bounced ?? 0), d: n0(s.emails.complained ?? 0) })}
        </div>
      )}
      {type === 'send_notification' && s.notifications && (
        <div className="jc-mail" title={t('«Принято» — канал взял сообщение; «доставлено» и «прочитано» — только по событию канала')}>
          {t('уведомлений {n} · €{c}', { n: n0(s.notifications.created), c: s.notifications.cost.replace(/0+$/, '').replace(/\.$/, '').replace('.', ',') })}
          {Object.entries(s.notifications.channels).map(([c, x]) => (
            <div key={c}>{t('{ch}: принято {a} · доставлено {b} · прочитано {r}{k}', { ch: t(CHANNEL_LABEL[c as Channel] ?? c), a: n0(x.accepted), b: n0(x.delivered), r: n0(x.read), k: x.skipped ? t(' · пропуск {n}', { n: n0(x.skipped) }) : '' })}</div>
          ))}
        </div>
      )}
      {outcomes.length > 0 && (
        <details className="jc-outcomes" onClick={(e) => e.stopPropagation()}>
          <summary>{t('исходы ({n})', { n: outcomes.length })}</summary>
          <ul>
            {outcomes.map(([o, n]) => (
              <li key={o}><span>{outcomeLabel(o)}</span><b>{n0(n)}</b></li>
            ))}
            {type === 'send_email' && s.emails && EMAIL_STAT_LABEL.filter(([k]) => s.emails![k]).map(([k, label]) => (
              <li key={`m-${k}`} className="muted"><span>{t('письма: {label}', { label: t(label) })}</span><b>{n0(s.emails![k])}</b></li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

function useNarrow() {
  const q = '(max-width: 640px)';
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(q).matches);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return;
    const on = () => setNarrow(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return narrow;
}

export interface CanvasProps {
  graph: Graph;
  stats?: JourneyStats | null;
  selected?: string | null;
  onSelect?: (id: string) => void;
  marks?: Record<string, Mark>;
  /** Steps removed in a diff (they are not in the graph; listed under the canvas). */
  removed?: string[];
  toolbar?: ReactNode;
}

export function JourneyCanvas({ graph, stats, selected, onSelect, marks = {}, removed = [], toolbar }: CanvasProps) {
  const narrow = useNarrow();
  const [view, setView] = useState<'canvas' | 'list' | null>(null);
  const mode = view ?? (narrow ? 'list' : 'canvas');
  return (
    <div className="jc">
      <div className="jc-bar">
        <div className="chips" role="group" aria-label={t('Режим просмотра')}>
          <button type="button" className={`chip${mode === 'canvas' ? ' on' : ''}`} onClick={() => setView('canvas')}>{t('Холст')}</button>
          <button type="button" className={`chip${mode === 'list' ? ' on' : ''}`} onClick={() => setView('list')}>{t('Список шагов')}</button>
        </div>
        <span className="spacer" />
        {toolbar}
      </div>
      {mode === 'canvas' ? <Canvas graph={graph} stats={stats} selected={selected} onSelect={onSelect} marks={marks} /> : <StepList graph={graph} stats={stats} selected={selected} onSelect={onSelect} marks={marks} />}
      {removed.length > 0 && (
        <div className="jc-removed small">{t('Удалены в этой версии:')} {removed.map((r) => <code key={r}>{r}</code>)}</div>
      )}
    </div>
  );
}

function Canvas({ graph, stats, selected, onSelect, marks }: Required<Pick<CanvasProps, 'graph' | 'marks'>> & Pick<CanvasProps, 'stats' | 'selected' | 'onSelect'>) {
  const { nodes, width, height, triggerX } = layoutGraph(graph);
  const at = new Map(nodes.map((n) => [n.id, n]));
  const TRIG_Y = 16;
  const TRIG_H = 64;
  const edges: { d: string; label: string; lx: number; ly: number; end?: { x: number; y: number }; key: string }[] = [];
  const startNode = at.get(graph.start);
  if (startNode) {
    const x1 = triggerX + NODE_W / 2;
    const y1 = TRIG_Y + TRIG_H;
    const x2 = startNode.x + NODE_W / 2;
    const y2 = startNode.y;
    edges.push({ key: 'trigger', d: `M ${x1} ${y1} C ${x1} ${(y1 + y2) / 2}, ${x2} ${(y1 + y2) / 2}, ${x2} ${y2}`, label: '', lx: 0, ly: 0 });
  }
  for (const n of nodes) {
    const outs = edgesOf(graph.steps[n.id]);
    outs.forEach((e, i) => {
      const x1 = n.x + (NODE_W * (i + 1)) / (outs.length + 1);
      const y1 = n.y + NODE_H;
      const target = e.to ? at.get(e.to) : null;
      if (!target) {
        const y2 = y1 + 34;
        edges.push({ key: `${n.id}-${e.key}`, d: `M ${x1} ${y1} L ${x1} ${y2 - 6}`, label: e.label, lx: x1, ly: y1 + 16, end: { x: x1, y: y2 } });
        return;
      }
      const x2 = target.x + NODE_W / 2;
      const y2 = target.y;
      const my = (y1 + y2) / 2;
      edges.push({ key: `${n.id}-${e.key}`, d: `M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}`, label: e.label, lx: (x1 + x2) / 2, ly: my });
    });
  }
  return (
    <div className="jc-scroll" tabIndex={0} aria-label={t('Холст сценария, прокручивается внутри рамки')}>
      <div className="jc-stage" style={{ width, height }}>
        <svg className="jc-edges" width={width} height={height} aria-hidden="true">
          <defs>
            <marker id="jc-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--border-strong)" />
            </marker>
          </defs>
          {edges.map((e) => (
            <g key={e.key}>
              <path d={e.d} fill="none" stroke="var(--border-strong)" strokeWidth={1.6} markerEnd={e.end ? undefined : 'url(#jc-arrow)'} />
              {e.end && (
                <g>
                  <circle cx={e.end.x} cy={e.end.y} r={5} fill="var(--surface)" stroke="var(--muted)" strokeWidth={1.5} />
                  <text x={e.end.x + 9} y={e.end.y + 4} className="jc-end">{t('конец')}</text>
                </g>
              )}
              {e.label && (
                <text x={e.lx} y={e.ly} textAnchor="middle" className="jc-label">{e.label}</text>
              )}
            </g>
          ))}
        </svg>
        <button type="button" className={`jc-node jc-trigger${selected === TRIGGER_NODE ? ' on' : ''}`} style={{ left: triggerX, top: TRIG_Y, width: NODE_W, height: TRIG_H }} onClick={() => onSelect?.(TRIGGER_NODE)} disabled={!onSelect}>
          <TriggerBody graph={graph} />
        </button>
        {nodes.map((n) => {
          const step = graph.steps[n.id];
          return (
            <div key={n.id} className={`jc-node t-${step.type}${selected === n.id ? ' on' : ''}${marks[n.id] ? ` m-${marks[n.id]}` : ''}${n.reachable ? '' : ' unreachable'}`} style={{ left: n.x, top: n.y, width: NODE_W, minHeight: NODE_H }}>
              <button type="button" className="jc-hit" onClick={() => onSelect?.(n.id)} disabled={!onSelect} aria-label={t('Шаг {id}: {label}', { id: n.id, label: t(STEP_LABEL[step.type]) })}>
                <NodeBody graph={graph} id={n.id} unreachable={!n.reachable} />
              </button>
              <NodeStats s={stats?.steps[n.id]} type={step.type} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The phone view and an accessible alternative: steps in reading order with their exits in words. */
export function StepList({ graph, stats, selected, onSelect, marks = {} }: Omit<CanvasProps, 'removed' | 'toolbar'>) {
  const order = stepsInOrder(graph);
  const exits = (s: Step) =>
    edgesOf(s)
      .map((e) => `${e.label ? `${e.label}: ` : ''}${e.to ?? t('конец')}`)
      .join(' · ');
  return (
    <ol className="jc-list">
      <li>
        <button type="button" className={`jc-row${selected === TRIGGER_NODE ? ' on' : ''}`} onClick={() => onSelect?.(TRIGGER_NODE)} disabled={!onSelect}>
          <span className="jc-kind">{t('Старт')} · {t(TRIGGER_LABEL[graph.trigger.type])}</span>
          <span className="jc-sum">{triggerText(graph.trigger)} → {graph.start}</span>
        </button>
      </li>
      {order.map((id) => {
        const s = graph.steps[id];
        return (
          <li key={id} className={`t-${s.type}${marks[id] ? ` m-${marks[id]}` : ''}`}>
            <button type="button" className={`jc-row${selected === id ? ' on' : ''}`} onClick={() => onSelect?.(id)} disabled={!onSelect}>
              <span className="jc-head">
                <span className="jc-icon"><StepIcon type={s.type} /></span>
                <span className="jc-kind">{t(STEP_LABEL[s.type])}</span>
                <code>{id}</code>
              </span>
              <span className="jc-sum">{stepSummary(s)}</span>
              {s.type !== 'exit' && <span className="jc-exits">→ {exits(s)}</span>}
            </button>
            <NodeStats s={stats?.steps[id]} type={s.type} />
          </li>
        );
      })}
    </ol>
  );
}
